import {  t  } from "@/client/libs/dummy-i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, ScrollArea, Separator, Tooltip } from "@stackmorph/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../shared/section-icon";

type Version = {
  id: string;
  name: string;
  createdAt: string;
};

export const VersionHistory = () => {
  const { resume } = useResumeStore();
  const queryClient = useQueryClient();
  const [restoreId, setRestoreId] = useState<string | null>(null);

  const { data: versions = [] } = useQuery({
    queryKey: ["versions", resume.id],
    queryFn: async () => {
      const response = await axios.get<Version[]>(`/resume/${resume.id}/version`);
      return response.data;
    },
  });

  const { mutate: createVersion, isPending: isCreating } = useMutation({
    mutationFn: async () => {
      await axios.post(`/resume/${resume.id}/version`, {
        name: t`Version ${format(new Date(), "PP p")}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["versions", resume.id] });
      toast({
        title: t`Version created`,
        description: t`A new version of your resume has been saved.`,
      });
    },
  });

  const { mutate: restoreVersion, isPending: isRestoring } = useMutation({
    mutationFn: async (versionId: string) => {
      await axios.post(`/resume/${resume.id}/version/${versionId}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resume", resume.id] });
      setRestoreId(null);
      toast({
        title: t`Version restored`,
        description: t`Your resume has been restored to the selected version.`,
      });
      // Reload page to reflect changes fully or rely on store update if implemented
      window.location.reload();
    },
  });

  const { mutate: deleteVersion } = useMutation({
    mutationFn: async (versionId: string) => {
      await axios.delete(`/resume/${resume.id}/version/${versionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["versions", resume.id] });
      toast({
        title: t`Version deleted`,
        description: t`The version has been removed.`,
      });
    },
  });

  return (
    <section id="version-history" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="version-history" name={t`Version History`} />
          <h2 className="line-clamp-1 text-3xl font-bold">{t`Version History`}</h2>
        </div>
      </header>

      <main className="grid gap-y-4">
        <p className="leading-relaxed opacity-75">
          {t`Save snapshots of your resume and restore them anytime. Useful for tracking changes or creating variations.`}
        </p>

        <Button disabled={isCreating} className="w-full" onClick={() => { createVersion(); }}>
          {t`Create New Version`}
        </Button>

        <Separator />

        <ScrollArea className="h-[400px] pr-4">
          <div className="grid gap-y-4">
            {versions.length === 0 && (
              <div className="text-center opacity-50 py-8">
                {t`No versions saved yet.`}
              </div>
            )}
            
            {versions.map((version) => (
              <div
                key={version.id}
                className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-secondary/50"
              >
                <div className="grid gap-y-1">
                  <span className="font-medium">{version.name}</span>
                  <span className="text-xs opacity-50">
                    {format(new Date(version.createdAt), "PP p")}
                  </span>
                </div>

                <div className="flex items-center gap-x-2">
                  <Tooltip content={t`Restore this version`}>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => { setRestoreId(version.id); }}
                    >
                      <RotateCcw size={14} />
                    </Button>
                  </Tooltip>

                  <Tooltip content={t`Delete version`}>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-error"
                      onClick={() => { deleteVersion(version.id); }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </main>

      <Dialog open={!!restoreId} onOpenChange={() => { setRestoreId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t`Restore Version`}</DialogTitle>
            <DialogDescription>
              {t`Are you sure you want to restore this version? Current changes will be overwritten.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setRestoreId(null); }}>
              {t`Cancel`}
            </Button>
            <Button
              variant="primary"
              disabled={isRestoring}
              onClick={() => restoreId && restoreVersion(restoreId)}
            >
              {t`Restore`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
